import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import TransactionModal from "./TransactionModal";
import CreditFilterDropdown from "./CreditFilterDropdown";
import CreditPurchaseModal from "./CreditPurchaseModal";
import {
  fetchAutoPurchase,
  fetchUserCreditBalance,
  fetchTokenSummary,
  getUserCreditHistory,
  getCreditHistoryDetails,
} from "../../apis/creditService";
import {
  CreditEntry,
  CreditEntryResponse,
  CreditHistoryDetail,
  TokenSummaryResponse,
} from "./types";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import TokensIcon from "../../assets/images/tokens.png";
import { useUserStore } from "../../store/userStore";
import { isLifetimeOgUser, isOgUser } from "../../utils/roles";
import { LifetimeOgTokenCard } from "../../components/OgBadges";
import DateRangeCalendar from "../Loads/DateRangeCalendar";
import { formatDateLocal } from "../Loads/searchStorage";
import { useClickOutside } from "../../hooks/useClickOutside";

interface CreditFilterParams {
  fromDate?: string;
  toDate?: string;
  isCreditSpent?: boolean;
  isCreditReceived?: boolean;
}

type SortOrder = string;

export interface CreditBalanceResponse {
  availableCredits: number;
  isAutoPurchase: boolean;
}

const Index = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const userRole = useUserStore((state) => state.role);
  const isLifetime = isLifetimeOgUser(userRole);
  const isOg = isOgUser(userRole) || isLifetime;
  const [sortOrder, setSortOrder] = useState<SortOrder>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleRowsPerPageChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const [creditHistory, setCreditHistory] = useState<CreditEntryResponse>({
    items: [],
    totalCount: 0,
    page: currentPage,
    pageSize: pageSize,
  });

  const [creditHistoryData, setCreditHistoryData] = useState<
    CreditEntry[] | null
  >();
  const [creditBalance, setCreditBalance] = useState<CreditBalanceResponse>();
  const [tokenSummary, setTokenSummary] = useState<TokenSummaryResponse | null>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<CreditHistoryDetail | null>(null);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Close the Credit Status dropdown on an outside click, and on navigating
  // away from this page (it previously stayed open in both cases).
  const creditFilterRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(creditFilterRef, () => setIsDropdownOpen(false), isDropdownOpen, "pointerdown");
  const location = useLocation();
  useEffect(() => {
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const creditTypeColors: { [key: string]: string } = {
    Respond: "#58BE74",
    Request: "#F1A560",
    Purchase: "#2892C5",
    Signup: "#90A9FF",
    Referral: "#EF8B87",
    Refund: "#14B8A6",
  };
  const [isFiltered, setIsFiltered] = useState(false);

  const [filters, setFilters] = useState<CreditFilterParams>({
    fromDate: "",
    toDate: "",
    isCreditSpent: undefined,
    isCreditReceived: undefined,
  });

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(creditHistory?.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const totalPages = Math.ceil(creditHistory?.totalCount / pageSize);
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);
    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - 4);
    }
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i,
    );
  };

  const fetchData = async () => {
    showLoader();
    try {
      const startTime = Date.now();
      const [balanceResponse, summaryResponse, historyResponse] = await Promise.all([
        fetchUserCreditBalance(),
        fetchTokenSummary(),
        getUserCreditHistory({
          page: currentPage,
          size: pageSize,
          sortBy: "CreatedDate",
          sortOrder: "desc",
        }),
      ]);
      let availableCredits = balanceResponse.availableCredits;
      if (balanceResponse.isAutoPurchase) {
        const autoPurchaseResult = await fetchAutoPurchase();
        if (autoPurchaseResult) {
          availableCredits = autoPurchaseResult.newUserCreditBalance;
        }
      }
      const elapsedTime = Date.now() - startTime;
      const delay = Math.max(500, 1000 - elapsedTime);

      setTimeout(() => {
        setCreditBalance({
          availableCredits: availableCredits,
          isAutoPurchase: balanceResponse.isAutoPurchase,
        });
        setTokenSummary(summaryResponse);
        setCreditHistory(historyResponse);
        setCreditHistoryData(historyResponse?.items);
        setLoading(false);
        hideLoader();
      }, delay);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    if (!isFiltered) {
      fetchData();
    } else {
      (async () => {
        try {
          showLoader();
          const filteredHistory = await getUserCreditHistory({
            page: currentPage,
            size: pageSize,
            sortBy: "CreatedDate",
            sortOrder: "desc",
            ...filters,
          });
          setCreditHistory(filteredHistory);
          setCreditHistoryData(filteredHistory?.items);
        } catch (error) {
          console.error("Failed to fetch paginated filtered data:", error);
        } finally {
          hideLoader();
        }
      })();
    }
  }, [currentPage, pageSize]);

  const handleViewDetails = async (id: number) => {
    try {
      const transactionDetails = await getCreditHistoryDetails(id);
      setSelectedTransaction(transactionDetails);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch credit history details:", error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const handleFilterApply = async (filters: CreditFilterParams) => {
    try {
      showLoader();
      setIsFiltered(true);
      setCurrentPage(1);
      setFilters({
        fromDate: filters.fromDate || "",
        toDate: filters.toDate || "",
        isCreditSpent: filters.isCreditSpent,
        isCreditReceived: filters.isCreditReceived,
      });

      const filteredHistory = await getUserCreditHistory({
        page: 1,
        size: pageSize,
        sortBy: "CreatedDate",
        sortOrder: "desc",
        ...filters,
      });

      setCreditHistory(filteredHistory);
      setCreditHistoryData(filteredHistory?.items);
    } catch (error) {
      console.error("Failed to fetch filtered credit history:", error);
    } finally {
      hideLoader();
    }
  };

  // Date range selector state (shared component from route search)
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    const dateStr = formatDateLocal(date);
    const dateIndex = selectedDates.findIndex(
      (d) => formatDateLocal(d) === dateStr,
    );
    if (dateIndex > -1) {
      setSelectedDates(selectedDates.filter((_, index) => index !== dateIndex));
    } else {
      setSelectedDates(
        [...selectedDates, date].sort((a, b) => a.getTime() - b.getTime()),
      );
    }
  };

  // Turn the calendar output (a "start - end" range string, or a list of
  // individual dates) into the fromDate/toDate the credit history API expects.
  const toDateRange = (dates: string[]) => {
    if (dates.length === 0) return { fromDate: "", toDate: "" };
    const first = dates[0];
    if (first.includes(" - ")) {
      const [fromDate, toDate] = first.split(" - ");
      return { fromDate, toDate };
    }
    const sorted = [...dates].sort();
    return { fromDate: sorted[0], toDate: sorted[sorted.length - 1] };
  };

  const handleSaveDates = (
    overrideDatesInRange?: string[],
    overrideSelectedDates?: Date[],
  ) => {
    const datesToSave =
      overrideDatesInRange ?? selectedDates.map((d) => formatDateLocal(d));
    const selDates = overrideSelectedDates ?? selectedDates;

    setDatesInRange(datesToSave);
    setSelectedDates(selDates);
    setIsCalendarOpen(false);

    const { fromDate, toDate } = toDateRange(datesToSave);
    handleFilterApply({ ...filters, fromDate, toDate });
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
    handleFilterApply({ ...filters, fromDate: "", toDate: "" });
  };

  const handleClearAll = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      isCreditSpent: undefined,
      isCreditReceived: undefined,
    });
    setIsFiltered(false);
    setCurrentPage(1);
    fetchData();
  };

  const totalPages = Math.ceil(creditHistory?.totalCount / pageSize);

  return (
    <div className="-m-6 ">
      {/* Token Summary Cards */}
      <div className="px-6 pt-6">
        <div className="token-summary-row flex flex-wrap items-stretch gap-2.5">
          {/* Total Tokens Card (Dark Card) */}
          {isLifetime ? (
            <LifetimeOgTokenCard />
          ) : (
            <div className="token-balance-card relative bg-[#141414] bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-[#45370C] via-[#141414] to-[#121212] border border-[#2B2412] rounded-2xl p-2.5 px-3.5 flex flex-col justify-between shadow-sm min-w-[210px]">
              <div className="flex items-center gap-3 mb-2">
                <img
                  src={TokensIcon}
                  alt="Tokens"
                  className="w-10.5 h-10.5 object-contain flex-shrink-0"
                />
                <div>
                  <p className="text-white text-2xl font-bold leading-none tracking-tight">
                    {tokenSummary?.totalTokens ?? creditBalance?.availableCredits ?? 0}
                  </p>
                  <p className="text-[#A1A1AA] text-xs mt-1 font-medium whitespace-nowrap">
                    Token Balance
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCreditPurchaseModal(true)}
                className="w-full bg-[#FFC72C] hover:bg-[#E5B01F] text-[#000000] text-xs font-bold py-1.5 px-3 rounded-lg transition-all text-center shadow-sm whitespace-nowrap"
              >
                Purchase Tokens
              </button>
            </div>
          )}

          {/* Tokens Earned / Spent Cards (White Cards, stacked) */}
          <div className="token-side-cards flex flex-col justify-between gap-2">
            <div className="token-side-card bg-white border border-[#F3F4F6] rounded-2xl py-2 px-3.5 flex items-center gap-3 shadow-sm min-w-[175px]">
              <div className="w-9 h-9 rounded-xl bg-[#EEF7D4] flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4.5 h-4.5 text-[#000000]"
                  viewBox="0 0 32 32"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* Stacked coins on left */}
                  <ellipse cx="10" cy="9" rx="5" ry="2.5" />
                  <path d="M5 9v3.5C5 13.9 7.2 15 10 15s5-1.1 5-2.5V9" />
                  <path d="M5 12.5v3.5C5 17.4 7.2 18.5 10 18.5s5-1.1 5-2.5v-3.5" />
                  {/* Up arrow on right */}
                  <path d="M22 21V9m0 0l-3.5 3.5M22 9l3.5 3.5" strokeWidth="2.2" />
                </svg>
              </div>
              <div>
                <p className="text-[#000000] text-xl font-bold leading-none tracking-tight">
                  {tokenSummary?.tokensEarned ?? 0}
                </p>
                <p className="token-side-card__label text-[#71717A] text-xs mt-0.5 font-medium whitespace-nowrap">
                  Total Tokens Earned
                </p>
              </div>
            </div>

            <div className="token-side-card bg-white border border-[#F3F4F6] rounded-2xl py-2 px-3.5 flex items-center gap-3 shadow-sm min-w-[175px]">
              <div className="w-9 h-9 rounded-xl bg-[#FDE8E8] flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4.5 h-4.5 text-[#000000]"
                  viewBox="0 0 32 32"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* Stacked coins on left */}
                  <ellipse cx="10" cy="9" rx="5" ry="2.5" />
                  <path d="M5 9v3.5C5 13.9 7.2 15 10 15s5-1.1 5-2.5V9" />
                  <path d="M5 12.5v3.5C5 17.4 7.2 18.5 10 18.5s5-1.1 5-2.5v-3.5" />
                  {/* Down arrow on right */}
                  <path d="M22 9v12m0 0l-3.5-3.5M22 21l3.5-3.5" strokeWidth="2.2" />
                </svg>
              </div>
              <div>
                <p className="text-[#000000] text-xl font-bold leading-none tracking-tight">
                  {tokenSummary?.tokensSpent ?? 0}
                </p>
                <p className="token-side-card__label text-[#71717A] text-xs mt-0.5 font-medium whitespace-nowrap">
                  Total Tokens Spent
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Token History Section */}
      <div className="px-6 mx-6 mt-6 rounded-md bg-white">
        {/* Header row */}
        <div className="token-history-header flex flex-wrap items-center justify-between gap-4 mb-4">
          <p className="token-history-title text-[#1F2937] text-lg font-semibold  mt-4">Token History</p>
          <div className="relative flex items-center gap-3">

            {/* Date range selector */}
            <div className="token-history-filter mt-5 min-w-[200px]">
              <DateRangeCalendar
                selectedDates={selectedDates}
                datesInRange={datesInRange}
                isCalendarOpen={isCalendarOpen}
                setIsCalendarOpen={setIsCalendarOpen}
                onDateChange={handleDateChange}
                onSaveDates={handleSaveDates}
                onClearDates={clearAllDates}
                minDate={null}
                maxDate={new Date()}
                className="h-11 border border-[#E5E7EB] bg-white rounded-lg"
              />
            </div>

            {/* Credit Status */}
            <div className="relative" ref={creditFilterRef}>
            <button
              type="button"
              onClick={() =>
                setIsDropdownOpen(!isDropdownOpen)
              }
              className="
                token-history-filter
                flex h-11 items-center gap-2
                rounded-lg
                border border-[#E5E7EB]
                bg-white
                px-3
                mt-5
                text-sm
                font-medium
                text-[#111827]
                transition-colors
                hover:bg-[#F9FAFB]
              "
            >
              <span>Token Status</span>

              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="m6 9 6 6 6-6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Existing filter dropdown */}
            {isDropdownOpen && (
              <CreditFilterDropdown
                isOpen={isDropdownOpen}
                onClose={closeDropdown}
                onApplyFilters={handleFilterApply}
                initialFromDate={filters.fromDate}
                initialToDate={filters.toDate}
                resultCount={creditHistory?.totalCount}
                initialStatus={
                  filters.isCreditSpent
                    ? "spent"
                    : filters.isCreditReceived
                      ? "received"
                      : ""
                }
              />
            )}
            </div>
          </div>
        </div>


        {/* ==================================================
    TOKENS HISTORY
================================================== */}

<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
  {creditHistoryData && creditHistoryData.length > 0 ? (
    creditHistoryData.map((item: CreditEntry) => {
      const creditType = item.creditType
        ? item.creditType.charAt(0).toUpperCase() +
          item.creditType.slice(1)
        : "Respond";

      const creditAmount = Math.abs(item.creditAmount ?? 0);
      const creditSign =
        item.creditType?.toLowerCase() === "request" ? "-" : "+";
      const isOgGrant = item.creditType?.toLowerCase() === "og_grant";
      // "View Details" opens a flight/response record, which only exists for
      // request/respond entries — other credit types (referral, og_grant,
      // admin_add, etc.) have nothing to show.
      const creditTypeLower = item.creditType?.toLowerCase() ?? "";
      const hasViewableDetails =
        creditTypeLower === "request" || creditTypeLower === "respond";

      const formattedDate = new Date(
        item.createdDate
      ).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

      const formattedTime = new Date(
        item.createdDate
      ).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      return (
        <div
          key={item.id}
          className="
            rounded-md
            border border-[#E5E7EB]
            bg-[#F8F8F8]
            p-2
          "
        >
          {/* ================================
              CARD HEADER
          ================================= */}

          <div className="mb-4 mx-2 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              {/* Credit Amount */}
              <span
                className="
                  inline-flex
                  h-8
                  min-w-[34px]
                  items-center
                  justify-center
                  rounded-md
                  bg-[#FFE8A3]
                  px-2
                  text-sm
                  font-bold
                  text-[#1F2937]
                "
              >
                {creditAmount}{creditSign}
              </span>

              {/* Load Status */}
              <span
                className="
                  truncate
                  text-sm
                  font-medium
                  text-[#374151]
                "
              >
{creditType}
              </span>
            </div>

            {/* Date */}
            <span
              className="
                shrink-0
                text-xs
                font-medium
                text-[#71809A]
              "
            >
              {formattedDate}, {formattedTime}
            </span>
          </div>

          {/* ================================
              AIRLINE / FLIGHT DETAILS
          ================================= */}

          <div
            className="
              flex
              min-h-[70px]
              items-center
              justify-between
              gap-3
              rounded-lg
              bg-white
              px-3
              py-2.5
            "
          >
            {/* Airline Information */}
            <div className="flex min-w-0 items-center gap-3">
              {item.airlineLogo && (
                <img
                  src={item.airlineLogo}
                  alt={item.airlineName ?? "Airline"}
                  className="h-9 w-9 shrink-0 rounded-md border border-[#E5E7EB] object-contain"
                />
              )}
              <div className="min-w-0">
                <p
                  className="
                    truncate
                    text-[15px]
                    font-medium
                    leading-5
                    text-[#111827]
                  "
                >
                  {item.airlineName ?? creditType}
                </p>
                {item.flightNumber && (
                  <p className="truncate text-xs font-medium text-[#71717A]">
                    {item.flightNumber}
                  </p>
                )}
              </div>
            </div>

            {/* View Details */}
            <button
              type="button"
              disabled={!hasViewableDetails || (isOg && isOgGrant)}
              onClick={() =>
                hasViewableDetails &&
                !(isOg && isOgGrant) &&
                handleViewDetails(item.id)
              }
              className={`
                flex
                h-11
                shrink-0
                items-center
                justify-center
                gap-2
                rounded-md
                border
                px-3
                text-sm
                font-medium
                transition-colors
                ${
                  !hasViewableDetails || (isOg && isOgGrant)
                    ? "cursor-not-allowed border-[#E5E7EB] bg-[#F8FAFC] text-[#9CA3AF]"
                    : "border-[#E5E7EB] bg-white text-[#17213A] hover:bg-[#F8FAFC]"
                }
              `}
            >
              {/* Eye icon */}
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7Z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                <circle
                  cx="12"
                  cy="12"
                  r="3"
                />
              </svg>

              <span>View Details</span>
            </button>
          </div>
        </div>
      );
    })
  ) : (
    <div className="col-span-full py-16 text-center">
      <p className="text-sm font-medium text-[#6B7280]">
        No token history available.
      </p>
    </div>
  )}
</div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-row flex-wrap items-center justify-between gap-3 mt-6 pb-6">
            <div className="flex items-center justify-start gap-2 text-sm text-[#6B7280] order-2 sm:order-1">
              <span className="whitespace-nowrap">
                Total {creditHistory?.totalCount}{" "}
                {creditHistory?.totalCount === 1 ? "record" : "records"}
              </span>
              <span className="whitespace-nowrap">Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  handleRowsPerPageChange(Number(e.target.value));
                  e.target.blur();
                }}
                className="border border-[#E5E7EB] rounded-md pl-3 pr-7 py-1 text-sm text-[#374151] cursor-pointer focus:outline-none bg-white"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>

            <nav className="order-1 sm:order-2">
              <ul className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-sm">
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center w-8 h-8 leading-tight text-primaryblue-50 border border-primaryblue-50 rounded-md disabled:opacity-50"
                  >
                    <i className="st-arrow-right text-2xl font-extrabold rotate-180"></i>
                  </button>
                </li>

                {getPageNumbers().map((pageNumber) => (
                  <li key={pageNumber}>
                    <button
                      onClick={() => handlePageChange(pageNumber)}
                      className={`flex items-center justify-center w-8 h-8 rounded-md ${
                        currentPage === pageNumber
                          ? "text-white border border-primaryblue-50 bg-primaryblue-50 font-extrabold"
                          : "text-primaryblue-50 border border-primaryblue-50"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  </li>
                ))}

                <li>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="flex items-center justify-center w-8 h-8 text-primaryblue-50 border border-primaryblue-50 rounded-md disabled:opacity-50"
                  >
                    <i className="st-arrow-right text-2xl font-extrabold"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreditPurchaseModal
        onPaymentComplete={fetchData}
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
      />

      {selectedTransaction && (
        <TransactionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          transaction={selectedTransaction}
        />
      )}
    </div>
  );
};

export default Index;
